import React from 'react';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import ContactCTA from '../components/ContactCTA';
import SEO from '../components/SEO';
import { combineSchemas, organizationSchema, serviceSchema, breadcrumbSchema } from '../lib/schema';
import { Search, FileText, Share2, Video, Mail, TrendingUp, Megaphone } from 'lucide-react';

const services = [
  { icon: Search, title: 'SEO Services', desc: 'On-page, technical and local SEO to rank higher for the searches that drive real revenue.', id: 'seo' },
  { icon: FileText, title: 'Content Writing', desc: 'Blog articles, landing pages and product copy written for both readers and search engines.', id: 'content' },
  { icon: Share2, title: 'Social Media', desc: 'Strategy, content calendars and paid social campaigns across Instagram, Facebook, LinkedIn & TikTok.', id: 'social' },
  { icon: Video, title: 'Video Advertising', desc: 'Short-form ads, brand films and YouTube campaigns that get watched and shared.', id: 'video' },
  { icon: Mail, title: 'Email Marketing', desc: 'Automated drip campaigns, newsletters and abandoned-cart recovery that boost lifetime value.' },
  { icon: TrendingUp, title: 'Paid Search (PPC)', desc: 'Google Ads & Microsoft Ads management focused on cost per lead, not vanity metrics.' },
];

const MarketingPage = () => (
  <Layout>
    <SEO
      title="Digital Marketing Services | SEO, PPC, Social & Content | Website Symphony"
      description="Inbound, paid and traditional marketing services: SEO, content writing, social media, video advertising, PPC and email marketing. Grow measurable ROI. Free strategy call."
      keywords="digital marketing agency, SEO services, PPC management, social media marketing, content marketing, video advertising, email marketing"
      canonical="/marketing"
      schema={combineSchemas(
        organizationSchema,
        serviceSchema('Digital Marketing Services', 'SEO, content, social media, video, PPC and email marketing services.', '/marketing'),
        breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Marketing', path: '/marketing' }]),
      )}
    />
    <PageHero
      title="Marketing Services"
      subtitle="Inbound, paid and traditional marketing built around one goal — measurable growth."
      breadcrumb="Marketing"
    />

    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#D9A04A]/15 mb-5">
            <Megaphone size={36} className="text-[#D9A04A]" />
          </div>
          <h2 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold mb-4">Marketing that moves the needle</h2>
          <p className="text-gray-600 max-w-3xl mx-auto text-[15px] leading-relaxed">
            From organic search to billboard production, our team blends digital, traditional and AI-powered marketing into one strategy designed to grow your business.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <div key={s.title} id={s.id} className="bg-[#F7F7F5] rounded-lg p-7 border-t-4 border-[#D9A04A] hover:shadow-xl transition-shadow">
              <s.icon className="text-[#2C5F66] mb-4" size={32} />
              <h3 className="text-[#2C5F66] text-lg font-bold mb-2">{s.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 bg-[#F7F7F5]">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <h3 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold mb-6">Results-First Reporting</h3>
        <p className="text-gray-600 text-[15px] mb-12">We report on the metrics that matter: qualified leads, cost per acquisition, return on ad spend and lifetime value.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[{ v: '4.2x', l: 'Avg. ROAS' }, { v: '63%', l: 'Avg. Traffic Lift' }, { v: '38%', l: 'Lower CPL' }, { v: '24/7', l: 'Reporting Access' }].map((s) => (
            <div key={s.l}>
              <div className="text-[#C68B30] text-4xl md:text-5xl font-extrabold">{s.v}</div>
              <div className="text-gray-600 mt-1 text-sm tracking-wider">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <ContactCTA defaultService="Marketing" heading="Grow faster, smarter, together" />
  </Layout>
);

export default MarketingPage;
